import React from 'react'
import { Box } from '@mui/material'
import { createTocContext, useTocContext } from './context'
import { NavItem, TOCProps } from './types'

export function TableOfContents<T extends NavItem>({
  items,
  selectedItemId,
  onItemSelect,
  itemWrapper = (c) => c,
}: TOCProps<T>) {
  const TocContext = createTocContext<T>()
  const contextValue = React.useMemo(() => {
    const currentPath = selectedItemId && getItemPath(selectedItemId, items)
    return {
      isSelectedItem: ({ id }: { id: string }) => id === selectedItemId,
      inActiveItemPath: ({ id }: { id: string }) => !!currentPath?.includes(id),
      onItemSelect: (item: T) => onItemSelect(item),
      itemWrapper,
    }
  }, [selectedItemId, items, onItemSelect, itemWrapper])

  return (
    <TocContext.Provider value={contextValue}>
      <Box>
        {items.map((item) => (
          <NavItemDisplay key={item.id} item={item} />
        ))}
      </Box>
    </TocContext.Provider>
  )
}

function NavItemDisplay<T extends NavItem>({ item }: { item: T }) {
  const { isSelectedItem, inActiveItemPath, onItemSelect, itemWrapper } =
    useTocContext<T>()
  const isSelected = isSelectedItem(item)
  const inActivePath = inActiveItemPath(item)
  const display = item.title.slice(0, 45)

  const [open, setOpen] = React.useState(inActivePath)
  const onSelect = (item: T) => {
    setOpen(!open)
    onItemSelect(item)
  }

  const itemComponent = (
    <Box padding={'3px'} border={'1px solid'} margin={'2px'}>
      <Box
        maxHeight={'45px'}
        style={{ backgroundColor: isSelected ? '#ddddff' : 'white' }}
      >
        <Box style={{ cursor: 'pointer' }} onClick={() => onSelect(item)}>
          <Box whiteSpace={'nowrap'} height={'50%'} overflow={'hidden'}>
            {!inActivePath && display}
            {inActivePath && <strong>{display}</strong>}
          </Box>
          {item.subtitle && <Box height={'50%'}>{item.subtitle}</Box>}
        </Box>
      </Box>
      {open && (
        <Box>
          {item.children.map((child) => (
            <NavItemDisplay key={child.id} item={child} />
          ))}
        </Box>
      )}
    </Box>
  )
  return itemWrapper(itemComponent, item)
}

export function getItemPath(
  itemId: string,
  items: readonly NavItem[],
): string[] | undefined {
  const ids: string[] | undefined = items.reduce(
    (acc: string[] | undefined, child) => {
      if (child.id === itemId) return [child.id]
      if (acc) return acc
      const ids = getItemPath(itemId, [...child.children])
      if (ids) return [child.id, ...ids]
      return acc
    },
    undefined,
  )
  return ids
}
